/**
 * Where a sign-in round trip comes back to, and how the route survives it.
 * ------------------------------------------------------------------------
 * Keycloak matches `redirect_uri` as a literal string against the client's
 * registered list, which holds the origin with and without a trailing slash and
 * no wildcard. keycloak-js defaults to `location.href` when `init` is given no
 * `redirectUri`, so opening any route other than the bare origin sent an
 * address that matches nothing and Keycloak refused the request.
 *
 * So the adapter is told to use the origin, and the route travels here instead:
 * stored before the redirect, taken back after it.
 */

/** Per tab, and gone when it closes — this outlives one redirect, nothing more. */
const STORAGE_KEY = 'mlp_return_path';

/** Structural, not `Storage`: this module is pure and its spec runs without a DOM. */
export interface PathStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** A relative path, whose second character is neither a slash nor a backslash. */
const RELATIVE_PATH = /^\/(?:[^/\\]|$)/;

/** C0 controls and DEL: a tab or newline inside a URL is a parser trick. */
// eslint-disable-next-line no-control-regex
const CONTROL_CHARACTER = /[\u0000-\u001F\u007F]/;

/** The only address the client registers; the route is carried separately. */
export const initRedirectUri = (location: { origin: string }): string =>
  location.origin;

/**
 * Same-origin relative paths only. A stored value becomes a navigation target,
 * so `//host` and `/\host` — both protocol-relative to a browser — are refused
 * rather than followed.
 */
export const isSafeReturnPath = (
  raw: string | null | undefined,
): raw is string =>
  typeof raw === 'string' &&
  RELATIVE_PATH.test(raw) &&
  !CONTROL_CHARACTER.test(raw);

/**
 * Storage throws rather than returns when a browser refuses it (private mode,
 * cookies off). Signing in must not depend on it, so every failure is a miss.
 */
export const quietly = <T,>(read: () => T, fallback: T): T => {
  try {
    return read();
  } catch {
    return fallback;
  }
};

/**
 * Is this location keycloak-js coming back, rather than somewhere a reader asked
 * for? It reads the callback out of the URL inside `init()`, which runs after we
 * have already seen the location, so the callback load looks ordinary and is not
 * the root. The test is the adapter's own for the standard flow — `(code or
 * error) and state`, keycloak-js lib/keycloak.js:1069 — and the parameters
 * arrive in the fragment because `responseMode` defaults to 'fragment' (`:68`).
 */
const isAuthCallback = (location: { search: string; hash: string }): boolean => {
  const carriesCallback = (raw: string): boolean => {
    const params = new URLSearchParams(raw.replace(/^[?#]/, ''));
    return (params.has('code') || params.has('error')) && params.has('state');
  };
  return carriesCallback(location.search) || carriesCallback(location.hash);
};

/** Remembers where the reader asked to be, unless that is already the root. */
export const rememberReturnPath = (
  location: { pathname: string; search: string; hash: string },
  storage: PathStorage,
): void => {
  if (isAuthCallback(location)) return;
  const path = `${location.pathname}${location.search}${location.hash}`;
  if (path === '/' || !isSafeReturnPath(path)) return;
  quietly(() => storage.setItem(STORAGE_KEY, path), undefined);
};

/** Signing out ends the route too — the next reader here may be someone else. */
export const forgetReturnPath = (storage: PathStorage): void => {
  quietly(() => storage.removeItem(STORAGE_KEY), undefined);
};

/**
 * Reads and clears in one step: a value left behind would bounce a later,
 * unrelated load to wherever the last redirect started.
 */
export const takeReturnPath = (storage: PathStorage): string | null => {
  const stored = quietly(() => storage.getItem(STORAGE_KEY), null);
  quietly(() => storage.removeItem(STORAGE_KEY), undefined);
  return isSafeReturnPath(stored) ? stored : null;
};
